import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toastApiError } from './toast-api-error';
import { apiClient } from '../client';
import { surveyKeys, processKeys, dashboardKeys, taskKeys } from '../query-keys';
import { dropRevokedProcessRead, invalidateProcessStatus } from '../process-read-cache';
import {
  submitSurveyContract,
  submitVariantResponseContract,
  type ListTasksResponse,
  type SubmitSurveyRequest,
  type SubmitSurveyResponse,
  type SubmitVariantResponseRequest,
  type SubmitVariantResponseResponse
} from '@bliks-io/contracts';

/**
 * Everything a survey answer makes stale, minus the process itself — that one
 * is decided per answer, because a decline may have revoked the read.
 */
function invalidateAnsweredSurvey(
  queryClient: ReturnType<typeof useQueryClient>,
  surveyId?: string
) {
  if (surveyId) {
    queryClient.invalidateQueries({ queryKey: surveyKeys.detail(surveyId) });
  }
  queryClient.invalidateQueries({ queryKey: surveyKeys.lists() });
  // My Tasks: an answered row leaves the task list.
  queryClient.invalidateQueries({ queryKey: taskKeys.all });
  queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
  invalidateProcessStatus(queryClient);
}

/**
 * The invalidated task list refetches later, and My Tasks counts what is left
 * off the cached one — so a finished row leaves it with the answer, before the
 * next survey can be answered against a list that still offers this one.
 */
function dropPendingSurvey(queryClient: ReturnType<typeof useQueryClient>, surveyId: string) {
  queryClient.setQueryData<ListTasksResponse>(
    taskKeys.all,
    (tasks) =>
      tasks && {
        ...tasks,
        pendingSurveys: tasks.pendingSurveys.filter((survey) => survey._id !== surveyId)
      }
  );
}

/**
 * Submit survey mutation
 *
 * Submits the classic questions (rating, frequency, duration). The Variant
 * Check answer has its own endpoint (plan 029) — see `useSubmitVariantResponse`.
 *
 * @example
 * ```tsx
 * function SurveyForm({ surveyId }: { surveyId: string }) {
 *   const submitSurvey = useSubmitSurvey();
 *
 *   const handleSubmit = (data: SubmitSurveyRequest) => {
 *     submitSurvey.mutate({ surveyId, data });
 *   };
 *
 *   return <form onSubmit={handleSubmit}>...</form>;
 * }
 * ```
 */
export function useSubmitSurvey() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      surveyId,
      data
    }: {
      surveyId: string;
      data: SubmitSurveyRequest;
    }): Promise<SubmitSurveyResponse> => {
      const path = submitSurveyContract.path.replace(':id', surveyId);
      const { data: response } = await apiClient.post<SubmitSurveyResponse>(path, data);
      return response;
    },
    onSuccess: (_, { surveyId }) => {
      dropPendingSurvey(queryClient, surveyId);
      invalidateAnsweredSurvey(queryClient, surveyId);
      // Show the updated survey data (average ratings) on the process.
      queryClient.invalidateQueries({ queryKey: processKeys.all });
    }
  });
}

/**
 * Submit Variant Check answer mutation
 *
 * The answer is a document of its own, keyed on the step recording rather than
 * on a survey row (plan 029), so it is recorded the moment it is given — the
 * classic survey around it may never be finished.
 *
 * Answering "Ich mache diesen Schritt nicht" is also the decline of a who-else
 * nomination (ADR 0023 decision 1), and a declined nomination no longer grants
 * process read (ADR 0014) — so that answer evicts the process instead of
 * refreshing it, exactly as the retired reject endpoint did. `processId` is a
 * variable rather than something read off the response: the answer carries the
 * bare recording id, not a populated process.
 *
 * The eviction is keyed on the ANSWER, not on the responder holding a
 * nomination — the client cannot tell, since the applicability fields are
 * stripped before the wire. For a decliner who reads the process some other way
 * (team membership, an assignment) the drop is a refetch, never a loss.
 */
export function useSubmitVariantResponse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      recordingId,
      data
    }: {
      recordingId: string;
      data: SubmitVariantResponseRequest;
      /** The answered step's process; absent only if it was not populated. */
      processId: string | undefined;
      /** The survey row the dialog was opened on, whose detail cache goes stale. */
      surveyId?: string;
      /** The answer leaves nothing else to ask, so the row leaves My Tasks. */
      endsSurvey: boolean;
    }): Promise<SubmitVariantResponseResponse> => {
      const path = submitVariantResponseContract.path.replace(':id', recordingId);
      const { data: response } = await apiClient.post<SubmitVariantResponseResponse>(path, data);
      return response;
    },
    onError: (error) => {
      // A refused verdict is most often the stale client: someone else's answer,
      // a re-survey, or a second tab already moved the row on. The backend's own
      // error codes say which, so they carry the message.
      toastApiError(error, 'myTasks.variantAnswerFailed');
    },
    onSuccess: async (_, { data, processId, surveyId, endsSurvey }) => {
      if (endsSurvey && surveyId) dropPendingSurvey(queryClient, surveyId);
      invalidateAnsweredSurvey(queryClient, surveyId);

      if (data.answer === 'not_performed' && processId) {
        // Drops the detail and marks the family stale; a blanket invalidation
        // would refetch the very process the answer may have revoked.
        await dropRevokedProcessRead(queryClient, processId);
        return;
      }

      queryClient.invalidateQueries({ queryKey: processKeys.all });
    }
  });
}
